import { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  makeStyles,
  Box,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import { useChat } from '../../contexts';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { mgaApiRef, ConversationSummary } from '../../api/MgaApi';
import { RAG_ASSISTANT_ID } from '../../api/RagAssistantAdapter';
import { formatDate } from '../../util';
import useSWR from 'swr';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  searchBox: {
    marginBottom: theme.spacing(2),
  },
  conversationList: {
    maxHeight: '60vh',
    overflow: 'auto',
  },
  conversationItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

interface ConversationHistoryModalProps {
  open: boolean;
  onClose: () => void;
  context?: 'popout' | 'modal';
}

export const ConversationHistoryModal: FC<ConversationHistoryModalProps> = ({
  open,
  onClose,
  context = 'modal',
}) => {
  const classes = useStyles();
  const mgaApi = useApi(mgaApiRef);
  const alertApi = useApi(alertApiRef);
  const { setActiveConversationId } = useChat();
  const [searchQuery, setSearchQuery] = useState('');

  const { data: conversations, mutate } = useSWR<ConversationSummary[]>(
    open ? 'conversations' : null,
    () => mgaApi.listConversationSummaries(),
  );

  const filteredConversations =
    conversations?.filter(
      conv =>
        conv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || [];

  // deduplicate conversations by id to prevent duplicate key warnings
  const uniqueConversations = Array.from(
    new Map(filteredConversations.map(c => [c.id, c])).values(),
  );

  const handleSelectConversation = (conversationId: string) => {
    const selectedConversation = conversations?.find(
      conv => conv.id === conversationId,
    );

    if (selectedConversation?.assistant?.id === RAG_ASSISTANT_ID) {
      if (context === 'popout') {
        setActiveConversationId(conversationId);
      } else {
        // Open in RAG modal
        window.dispatchEvent(
          new CustomEvent('openRagModal', {
            detail: { conversationId, openSource: 'history' },
          }),
        );
      }
    } else {
      setActiveConversationId(conversationId);
    }

    onClose();
  };

  const handleDeleteConversation = async (
    e: React.MouseEvent,
    conversationId: string,
  ) => {
    e.stopPropagation();
    try {
      await mgaApi.deleteConversation(conversationId);
      await mutate();
    } catch {
      alertApi.post({
        message: 'Failed to delete conversation. Please try again.',
        severity: 'error',
        display: 'transient',
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box className={classes.dialogTitle}>
          <Typography variant="h6">Conversation History</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          className={classes.searchBox}
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {uniqueConversations.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body1">
              {searchQuery
                ? 'No conversations found'
                : 'No conversation history'}
            </Typography>
          </Box>
        ) : (
          <List className={classes.conversationList}>
            {uniqueConversations.map(conversation => (
              <ListItem
                key={conversation.id}
                className={classes.conversationItem}
                onClick={() => handleSelectConversation(conversation.id)}
              >
                <ListItemText
                  primary={conversation.title}
                  secondary={
                    <>
                      {conversation.assistant?.name} •{' '}
                      {formatDate(conversation.updatedAt)}
                      {conversation.messageCount &&
                        ` • ${conversation.messageCount} messages`}
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={e => handleDeleteConversation(e, conversation.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};
